/**
 * Phase 5.4a (2026-05-15) — Lightweight IndexedDB wrapper for the
 * offline-first PWA. Pure vanilla (no `idb` dep added) to keep the bundle
 * lean.
 *
 * Database: `remobiles-offline-db` v2.
 * Stores:
 *   - `outbox_actions`   : queue FIFO of write-actions awaiting sync.
 *       key:        autoIncrement
 *       index:      `idempotency_key` (unique), `status`, `created_at`
 *       value:      { idempotency_key, method, url, body, status: 'pending'|'syncing'|'done'|'failed',
 *                     created_at, last_error, retry_count, response }
 *   - `cache_articles`   : keyed by `id` (Mongo UUID).
 *       STEP 3 multi-magasin (2026-05-15) — was keyed by `reference` in
 *       v1; bumped to v2 to accommodate the same `reference` existing in
 *       M1 + M2 (legacy data is dropped on the version upgrade; the next
 *       online sync rebuilds the cache from the API).
 *   - `cache_clients`    : keyed by `id` — clients
 *   - `cache_settings`   : keyed by `key` — single-row settings doc id=`default`
 *   - `cache_meta`       : last-sync timestamps etc., keyed by `key`
 */

const DB_NAME = 'remobiles-offline-db';
const DB_VERSION = 2;

const STORES = {
  outbox: 'outbox_actions',
  articles: 'cache_articles',
  clients: 'cache_clients',
  settings: 'cache_settings',
  meta: 'cache_meta',
};

let _dbPromise = null;

const openDB = () => {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unsupported in this browser'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      // STEP 3 (v1 → v2) — `cache_articles` keyPath flipped to `id`.
      // The article cache is rebuilt online anyway, so the safest thing
      // is to drop the legacy store. New devices on v2 directly create
      // the store with the new keyPath.
      if (event.oldVersion < 2 && db.objectStoreNames.contains(STORES.articles)) {
        db.deleteObjectStore(STORES.articles);
      }
      if (!db.objectStoreNames.contains(STORES.outbox)) {
        const s = db.createObjectStore(STORES.outbox, { keyPath: 'id', autoIncrement: true });
        s.createIndex('idempotency_key', 'idempotency_key', { unique: true });
        s.createIndex('status', 'status', { unique: false });
        s.createIndex('created_at', 'created_at', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.articles)) {
        // v2: key by Mongo UUID `id` (was `reference` in v1).
        const s = db.createObjectStore(STORES.articles, { keyPath: 'id' });
        s.createIndex('reference_magasin', ['reference', 'magasin_id'], { unique: false });
        s.createIndex('ean_magasin', ['ean', 'magasin_id'], { unique: false });
        s.createIndex('magasin_id', 'magasin_id', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.clients)) {
        db.createObjectStore(STORES.clients, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.settings)) {
        db.createObjectStore(STORES.settings, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORES.meta)) {
        db.createObjectStore(STORES.meta, { keyPath: 'key' });
      }
    };
    req.onsuccess = (event) => resolve(event.target.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
};

const runReq = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const tx = async (storeName, mode = 'readonly') => {
  const db = await openDB();
  const transaction = db.transaction(storeName, mode);
  return transaction.objectStore(storeName);
};

// ── Outbox actions ──────────────────────────────────────────────────────
export const addOutboxAction = async (action) => {
  const now = Date.now();
  const doc = {
    idempotency_key: action.idempotency_key,
    method: action.method || 'POST',
    url: action.url,
    body: action.body || null,
    status: 'pending',
    created_at: now,
    last_error: null,
    retry_count: 0,
    response: null,
  };
  const store = await tx(STORES.outbox, 'readwrite');
  await runReq(store.add(doc));
  return doc;
};

export const updateOutboxAction = async (id, patch) => {
  const store = await tx(STORES.outbox, 'readwrite');
  const current = await runReq(store.get(id));
  if (!current) return null;
  const next = { ...current, ...patch };
  await runReq(store.put(next));
  return next;
};

export const listOutbox = async (statusFilter) => {
  const store = await tx(STORES.outbox);
  const all = await runReq(store.getAll());
  if (!statusFilter) return all.sort((a, b) => a.created_at - b.created_at);
  return all.filter((a) => a.status === statusFilter).sort((a, b) => a.created_at - b.created_at);
};

export const countOutbox = async (statusFilter) => {
  const list = await listOutbox(statusFilter);
  return list.length;
};

export const deleteOutboxAction = async (id) => {
  // NB: "delete" here means remove from IndexedDB; nothing happens server-side.
  // This is OK — outbox is purely client transport state, not user data.
  const store = await tx(STORES.outbox, 'readwrite');
  await runReq(store.delete(id));
};

export const clearDoneOutbox = async () => {
  const store = await tx(STORES.outbox, 'readwrite');
  const all = await runReq(store.getAll());
  for (const a of all) {
    if (a.status === 'done') {
      await runReq(store.delete(a.id));
    }
  }
};

// ── Cache helpers (articles / clients / settings) ───────────────────────
export const replaceCacheBulk = async (storeKey, rows, keyAttr) => {
  const store = await tx(storeKey, 'readwrite');
  await runReq(store.clear());
  for (const row of rows || []) {
    if (!row[keyAttr]) continue;
    await runReq(store.put(row));
  }
};

export const cacheArticles = (rows) => replaceCacheBulk(STORES.articles, rows, 'reference');
export const cacheClients = (rows) => replaceCacheBulk(STORES.clients, rows, 'id');
export const cacheSettings = async (settings) => {
  // NB: put-by-key overwrites, no need to clear (and clearing would wipe
  // sibling keys like `caisse_status`).
  const store = await tx(STORES.settings, 'readwrite');
  await runReq(store.put({ key: 'default', ...settings }));
};

// Phase 5.4a-fix (2026-05-15) — caisse status cache so the POS keeps
// working offline. Frontend writes here on every successful GET /caisse/status,
// and reads from here when offline (`navigator.onLine === false`).
export const cacheCaisseStatus = async (status) => {
  const store = await tx(STORES.settings, 'readwrite');
  await runReq(store.put({
    key: 'caisse_status',
    is_open: !!status?.is_open,
    caisse_id: status?.caisse_id || null,
    opened_at: status?.opened_at || null,
    opened_by: status?.opened_by || null,
    cached_at: Date.now(),
  }));
};

export const getCachedCaisseStatus = async () => {
  const store = await tx(STORES.settings);
  return runReq(store.get('caisse_status'));
};

export const getCachedArticles = async () => {
  const store = await tx(STORES.articles);
  return runReq(store.getAll());
};

export const getCachedClients = async () => {
  const store = await tx(STORES.clients);
  return runReq(store.getAll());
};

export const getCachedSettings = async () => {
  const store = await tx(STORES.settings);
  return runReq(store.get('default'));
};

// ── Meta (last sync, etc.) ──────────────────────────────────────────────
export const setMeta = async (key, value) => {
  const store = await tx(STORES.meta, 'readwrite');
  await runReq(store.put({ key, value, updated_at: Date.now() }));
};

export const getMeta = async (key) => {
  const store = await tx(STORES.meta);
  const row = await runReq(store.get(key));
  return row?.value;
};

export const STORE_NAMES = STORES;
